import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { Router } from "@angular/router";
import { Subscription, firstValueFrom } from "rxjs";
import { BookingStatus, BookingStatusStrings } from "src/app/core/constants/bookingConstant";
import { Helper } from "src/app/core/helpers/helper";
import { BookingDetailsModalComponent } from "../booking-details/booking-details.component";
import { HistoryService } from "src/app/shared/services/history.service";
import { IncomingCallService, IncomingCallSnapshot } from "src/app/shared/services/incoming-call.service";
import { VehicleTrackingService } from "src/app/shared/services/vehicle-tracking.service";

/**
 * Spec #1 — Incoming Call popup.
 *
 * Single, app-wide modal mounted from the layout shell. Driven by
 * IncomingCallService — the modal itself owns no state, just a template ref
 * and the open/close lifecycle. The service runs the customer→driver search
 * ladder; the template renders whatever the current snapshot says.
 *
 * Decisions reflected here:
 *  - Always-visible "Search as driver" / "Search as customer" buttons so the
 *    dispatcher can pivot at will even when a default match was found.
 *  - "Open in Quick Booking" routes via `?phone=...` so the existing
 *    auto-search in QuickBooking takes over (no duplicate logic).
 *  - "Use as new" routes to Quick Booking with the same param — Quick Booking
 *    will show the "not found / register" flow.
 */
@Component({
  selector: "app-incoming-call-modal",
  templateUrl: "./incoming-call-modal.component.html",
  styleUrl: "./incoming-call-modal.component.scss",
  standalone: false
})
export class IncomingCallModalComponent implements OnInit, OnDestroy {
  @ViewChild("modalTemplate", { static: true }) modalTemplate!: TemplateRef<any>;
  @ViewChild("callerBookingDetails") callerBookingDetails?: BookingDetailsModalComponent;

  snapshot: IncomingCallSnapshot = { state: "idle", event: null, customer: null, driver: null };
  currentLive = "";
  currentEta = "";
  modalRef?: NgbModalRef;
  private subs: Subscription[] = [];
  private loadedActiveId = "";

  constructor(
    public helper: Helper,
    private modalService: NgbModal,
    private router: Router,
    private incomingCall: IncomingCallService,
    private tracking: VehicleTrackingService,
    private historyService: HistoryService
  ) {}

  ngOnInit(): void {
    this.subs.push(
      this.incomingCall.state$().subscribe((snap) => {
        this.snapshot = snap;
        this.syncCurrentTrip(snap);
        // Caller hung up / call was missed → service drops to idle with no
        // event. Close the open modal (if any) so the dispatcher isn't left
        // staring at a stale popup.
        if (this.modalRef && snap.state === "idle" && !snap.event) {
          this.modalRef.dismiss();
        }
      })
    );
    this.subs.push(this.incomingCall.onOpen$().subscribe(() => this.openModal()));
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  /** Open if not already; do nothing on repeat open events for the same call. */
  private openModal(): void {
    if (this.modalRef) return;
    this.modalRef = this.modalService.open(this.modalTemplate, {
      centered: true,
      backdrop: "static",
      keyboard: false,
      size: "lg",
      scrollable: true
    });
    this.modalRef.result.then(
      () => this.cleanup(),
      () => this.cleanup()
    );
  }

  private cleanup(): void {
    this.modalRef = undefined;
    this.incomingCall.dismiss();
  }

  /** Compose the "+91 9876543210" header label. */
  callerDisplay(): string {
    if (!this.snapshot.event) return "";
    const code = this.snapshot.event.fromCountryPhoneCode || "";
    return `${code ? code + " " : ""}${this.snapshot.event.fromNumber}`;
  }

  /** Manual pivot — also try driver lookup. */
  searchAsDriver(): void {
    this.incomingCall.lookupAsDriver();
  }

  /** Manual pivot — go back to the customer view. */
  searchAsCustomer(): void {
    this.incomingCall.lookupAsCustomer();
  }

  /** Open Quick Booking with the caller's phone pre-filled. */
  openQuickBooking(): void {
    const ev = this.snapshot.event;
    if (!ev) return;
    const queryParams: any = { phone: ev.fromNumber };
    if (ev.fromCountryPhoneCode) queryParams.countryPhoneCode = ev.fromCountryPhoneCode;
    // Carry the callId so Quick Booking can correlate the call lifecycle —
    // when the caller hangs up the service emits `onCallEnded$(callId)` and
    // Quick Booking flips its banner to "Call ended".
    if (ev.callId) queryParams.callId = ev.callId;
    this.modalRef?.close();
    // `queryParamsHandling: ""` makes Angular replace (not merge) query params,
    // so an old `?phone=...` from a previous call is overwritten.
    // The QuickBooking component subscribes to queryParamMap so this triggers
    // even when the user is already on /users/quick-booking.
    this.router.navigate(["/users/quick-booking"], { queryParams, queryParamsHandling: "" });
  }

  /** Open the driver detail page (or vehicle tracking with that driver). */
  openDriverProfile(): void {
    const driver = this.snapshot.driver;
    if (!driver?._id) return;
    this.modalRef?.close();
    this.router.navigate(["/users/vehicle-tracking"], { queryParams: { driverId: driver._id } });
  }

  close(): void {
    this.modalRef?.dismiss();
  }

  statusKey(status: number): string {
    return (BookingStatusStrings as Record<number, string>)[Number(status)] || "";
  }

  tripRoute(trip: any): string {
    const pickup = String(trip?.pickupAddress?.address || "");
    const dests = trip?.destinationAddresses || [];
    const drop = String(dests[dests.length - 1]?.address || "");
    const short = (value: string): string => value.split(",")[0].trim() || "—";
    return `${short(pickup)} → ${short(drop)}`;
  }

  /** One click opens that trip's Vehicle Track (8) or Logs (7) on the details page. */
  openTrip(trip: any, tab: number): void {
    const id = String(trip?._id || "");
    if (!id) return;
    const status = Number(trip?.status);
    const archived =
      status === BookingStatus.CANCELLED ||
      status === BookingStatus.MERCHANT_REJECTED ||
      status >= BookingStatus.COMPLETED;
    const type = archived ? this.helper.BookingHistoryMainType.COMPLETED : this.helper.BookingHistoryMainType.RUNNING;
    this.callerBookingDetails?.openModal(type, id, tab);
  }

  private syncCurrentTrip(snap: IncomingCallSnapshot): void {
    const booking = snap.activeBooking;
    if (!booking?._id) {
      this.currentLive = "";
      this.currentEta = "";
      this.loadedActiveId = "";
      return;
    }
    if (this.loadedActiveId === String(booking._id)) return;
    this.loadedActiveId = String(booking._id);
    this.currentLive = this.liveLabel(this.pointFromGeo(booking.confirmedDriver?.location));
    this.currentEta = this.etaLabel(booking) || "ETA …";
    void this.loadCurrentExtras(booking);
  }

  private async loadCurrentExtras(booking: any): Promise<void> {
    const id = String(booking._id);
    try {
      if (booking.uniqueId != null && booking.uniqueId !== "") {
        const res: any = await firstValueFrom(this.tracking.getTripByNumber(booking.uniqueId));
        if (this.loadedActiveId !== id) return;
        const data = res?.data?.data || res?.data || {};
        const live = data.driver?.currentLocation;
        const label = this.liveLabel(live);
        if (label) this.currentLive = label;
      }
    } catch {
      /* the assigned location above still stands */
    }
    try {
      const res: any = await firstValueFrom(this.historyService.getRunningBookingDetails(id));
      if (this.loadedActiveId !== id) return;
      const full = res?.booking || booking;
      const eta = this.etaLabel(full);
      this.currentEta = eta || "ETA unavailable";
    } catch {
      if (this.loadedActiveId === id && this.currentEta === "ETA …") this.currentEta = this.etaLabel(booking) || "ETA unavailable";
    }
  }

  /** `bookingInvoice.estimated.time` is the trip estimate already stored in seconds. */
  private etaLabel(booking: any): string {
    const seconds = Number(booking?.bookingInvoice?.estimated?.time);
    if (!Number.isFinite(seconds) || seconds <= 0) return "";
    return `ETA ${Math.max(1, Math.round(seconds / 60))} min`;
  }

  private pointFromGeo(geo: any): { latitude: number; longitude: number } | null {
    const coords = geo?.coordinates;
    if (!Array.isArray(coords) || coords.length < 2) return null;
    const longitude = Number(coords[0]);
    const latitude = Number(coords[1]);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
    if (Math.abs(latitude) < 0.0001 && Math.abs(longitude) < 0.0001) return null;
    return { latitude, longitude };
  }

  private liveLabel(point: { latitude?: number; longitude?: number } | null): string {
    const latitude = Number(point?.latitude);
    const longitude = Number(point?.longitude);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return "";
    if (Math.abs(latitude) < 0.0001 && Math.abs(longitude) < 0.0001) return "";
    return `Live driver ${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
  }

  /**
   * Maps `EntityStatus` numeric codes returned by the driver lookup into the
   * matching translation key. Mirrors the server enum at
   * server/src/constants/constant.ts and is kept in sync with the colour
   * scheme used by the Dispatcher Panel driver list.
   */
  driverStatusLabel(status: number): string {
    switch (status) {
      case 1: return "label-title.status-pending";
      case 2: return "label-title.status-decline";
      case 3: return "label-title.status-offline";          // approved but offline
      case 4: return "label-title.status-blocked";
      case 6: return "label-title.status-available";
      case 7: return "label-title.status-in-booking";
      case 8: return "label-title.status-available-for-share";
      case 9: return "label-title.status-near-available";
      case 10: return "label-title.status-business-off";
      default: return "label-title.unknown";
    }
  }

  /** Bootstrap badge class matching the marker / list colour for this status. */
  driverStatusBadgeClass(status: number): string {
    switch (status) {
      case 6: return "bg-success";
      case 7: return "bg-warning text-dark";
      case 8: return "bg-primary";
      case 9: return "bg-info";
      case 3: return "bg-secondary";
      case 10: return "bg-dark";
      case 4: return "bg-danger";
      default: return "bg-secondary";
    }
  }
}
